import type { EntityContentGroupDefinitions } from '@backstage/plugin-catalog-react/alpha';
import type {
  DynamicSubRoute,
  DynamicSubRouteProps,
  SubRoute,
} from '@ipen-portal/plugin-common-react';
import type {
  ComponentType,
  JSX,
  PropsWithChildren,
  ReactElement,
} from 'react';

import { type Entity, stringifyEntityRef } from '@backstage/catalog-model';
import {
  coreExtensionData,
  createExtensionInput,
} from '@backstage/frontend-plugin-api';
import { useAsyncEntity } from '@backstage/plugin-catalog-react';
import { isNewsEntity } from '@ipen-portal/ipen-entity-model';
import { notEmpty } from '@ipen-portal/plugin-common';
import {
  DynamicEntityRouteBlueprint,
  EntityLayoutBlueprint,
  EntityToolbarElementBlueprint,
  hasToolbar,
  hasWrapper,
  isRouteWithMultipleTabs,
  MobileViewContext,
  SubRouteContext,
} from '@ipen-portal/plugin-common-react';
import {
  EntityContentWithToolbarBlueprint,
  IpenEntityLayout,
  OverviewPageToolbar,
  PAGE_HEADER_SIZE,
} from '@ipen-portal/plugin-layout-react';
import { SubRouteNavigation } from '@ipen-portal/plugin-navigation-react';
import { sortBy } from 'es-toolkit';
import { useContext, useMemo } from 'react';
import { useParams } from 'react-router';
import { z } from 'zod';

import type { DynamicRoute } from '../hooks/useResolveDynamicRoutes';

import { useResolveDynamicRoutes } from '../hooks/useResolveDynamicRoutes';
import { useSelectedSubRoute } from '../hooks/useSelectedSubRoute';

const HEADER_HEIGHT = 56;

const dynamicRoutes = createExtensionInput([
  DynamicEntityRouteBlueprint.dataRefs.component,
  DynamicEntityRouteBlueprint.dataRefs.filterFunction.optional(),
]);

const layoutWrapper = createExtensionInput([
  EntityContentWithToolbarBlueprint.dataRefs.toolbarElements,
  EntityContentWithToolbarBlueprint.dataRefs.filterFunction,
  EntityContentWithToolbarBlueprint.dataRefs.layoutWrapperComponent.optional(),
  coreExtensionData.routePath,
]);

const toolbarElements = createExtensionInput([
  EntityToolbarElementBlueprint.dataRefs.component,
  EntityToolbarElementBlueprint.dataRefs.filterFunction.optional(),
  coreExtensionData.routePath,
]);

type DynamicRouteDefinition = {
  Component: (props: DynamicSubRouteProps) => JSX.Element;
  filter?: (entity: Entity) => boolean;
  id: string;
};

type EntityListWrapper = {
  filter?: (entity: Entity) => boolean;
  id: string;
  LayoutWrapperComponent?: ComponentType<PropsWithChildren<{ entity: Entity }>>;
  path?: string;
  ToolbarElements: ComponentType<PropsWithChildren<{ entity: Entity }>>;
}[];

export const IpenEntityLayoutExtension =
  EntityLayoutBlueprint.makeWithOverrides({
    configSchema: {
      navOrder: z.array(z.string()).optional(),
    },
    factory(originalFactory, { config, inputs }) {
      const dynamicRouteDefinitions = inputs.dynamicRoutes.map(item => ({
        Component: item.get(DynamicEntityRouteBlueprint.dataRefs.component),
        filter: item.get(DynamicEntityRouteBlueprint.dataRefs.filterFunction),
        id: item.node.spec.id,
      }));

      const entityListWrapper = inputs.layoutWrapper.map(item => ({
        filter: item.get(
          EntityContentWithToolbarBlueprint.dataRefs.filterFunction,
        ),
        id: item.node.spec.id,
        LayoutWrapperComponent: item.get(
          EntityContentWithToolbarBlueprint.dataRefs.layoutWrapperComponent,
        ),
        path: item.get(coreExtensionData.routePath),
        ToolbarElements: item.get(
          EntityContentWithToolbarBlueprint.dataRefs.toolbarElements,
        ),
      }));

      const navOrder =
        config.navOrder?.reduce(
          (acc, curr, index) => {
            acc[curr.toLowerCase()] = index;
            return acc;
          },
          {} as Record<string, number>,
        ) ?? {};
      return originalFactory({
        loader:
          async () =>
            ({
              groupDefinitions,
              routes: staticRoutes,
            }: {
              groupDefinitions: EntityContentGroupDefinitions;
              routes: readonly DynamicSubRoute[];
            }) => {
              const { kind, name, namespace } = useParams();
              const entityRefFromUrl =
                kind && name
                  ? stringifyEntityRef({ kind, name, namespace })
                  : 'unknown-entity';

              const { entity, loading } = useAsyncEntity();

              const filteredDynamicRouteDefinitions = useMemo(
                () => filterDynamicRoutes({ dynamicRouteDefinitions, entity }),
                [entity],
              );

              const { dynamicRoutes, dynamicRoutesElement } =
                useResolveDynamicRoutes(filteredDynamicRouteDefinitions);

              const routes: SubRoute[] = useMemo(() => {
                if (!entity || entityRefFromUrl !== stringifyEntityRef(entity)) {
                // if the entity does not match to the url, we need to reset the routes
                  return [];
                }
                return combineAndSortRoutes({
                  dynamicRoutes,
                  navOrder,
                  staticRoutes,
                });
              }, [dynamicRoutes, staticRoutes, entity, entityRefFromUrl]);

              const groups: EntityContentGroupDefinitions = useMemo(() => {
                if (!entity || entityRefFromUrl !== stringifyEntityRef(entity)) {
                // if the entity does not match to the url, we need to reset the routes
                  return groupDefinitions;
                }

                return mergeDynamicGroups({ dynamicRoutes, groupDefinitions });
              }, [dynamicRoutes, groupDefinitions, entity, entityRefFromUrl]);

              const { element, isFallbackRoute, route, selectedSubPath } =
                useSelectedSubRoute(routes);

              const routesLoading =
                loading || Object.values(dynamicRoutes).some(r => r.loading);

              const subRouteState = useMemo(
                () => ({
                  loading: routesLoading,
                  selectedSubPath,
                  selectedSubRoute: route,
                  subRoutes: routes,
                }),
                [routesLoading, selectedSubPath, routes, route],
              );

              const { isMobileView } = useContext(MobileViewContext);

              const filteredEntityListWrapper = useMemo(
                () =>
                  filterEntityListByRoute({ entity, entityListWrapper, route }),
                [route, entity],
              );

              const contextProvider = useMemo(
                () =>
                  getEntityLayoutProviders({
                    filteredEntityListWrapper,
                    route,
                  }),
                [filteredEntityListWrapper, route],
              );

              // we do not render the sub navigation on the news entity page
              const hideNav = isMobileView || isNewsEntity(entity);
              const subNavigation = hideNav
                ? undefined
                : (
                    <SubRouteNavigation
                      groupDefinitions={groups}
                      loading={routesLoading}
                      maxHeightOffset={HEADER_HEIGHT}
                      minHeightOffset={PAGE_HEADER_SIZE - HEADER_HEIGHT}
                      selectedSubPath={selectedSubPath}
                      selectedSubRoute={route}
                      subRoutes={routes}
                      titleOrder={navOrder}
                    />
                  );

              const title = getTitleFromRoute({
                groups,
                route,
                selectedSubPath,
              });

              const toolbarElementDefinitions = inputs.toolbarElements.map(
                item => ({
                  Component: item.get(
                    EntityToolbarElementBlueprint.dataRefs.component,
                  ),
                  filter: item.get(
                    EntityToolbarElementBlueprint.dataRefs.filterFunction,
                  ),
                  id: item.node.spec.id,
                  path: item.get(coreExtensionData.routePath),
                }),
              );

              return (
                <>
                  {dynamicRoutesElement}
                  <SubRouteContext.Provider value={subRouteState}>
                    <IpenEntityLayout
                      contextProvider={contextProvider}
                      groupDefinitions={groups}
                      subNavigation={subNavigation}
                      subtitle={title}
                      titleOrder={navOrder}
                      toolbar={(
                        <Toolbar
                          entity={entity}
                          filteredEntityListWrapper={filteredEntityListWrapper}
                          route={route}
                          toolbarElementDefinitions={toolbarElementDefinitions}
                        />
                      )}
                    >
                      {isFallbackRoute && (
                      // For the Broken-Link-Crawler
                        <div style={{ display: 'none' }}>
                          ERROR 404: PAGE NOT FOUND
                        </div>
                      )}
                      {element}
                    </IpenEntityLayout>
                  </SubRouteContext.Provider>
                </>
              );
            },
        order: 10,
      });
    },
    inputs: {
      dynamicRoutes,
      layoutWrapper,
      toolbarElements,
    },
    name: 'ipen-layout',
  });

export const getTitleFromRoute = ({
  groups,
  route,
  selectedSubPath,
}: {
  groups: EntityContentGroupDefinitions;
  route: SubRoute | undefined;
  selectedSubPath: string | undefined;
}) => {
  let title: string | undefined;
  let group: string | undefined =
    route && 'group' in route ? route.group : undefined;
  if (isRouteWithMultipleTabs(route)) {
    const tab = route.tabs.find(r => r.subPath === selectedSubPath);
    title = tab?.title;
    group = tab?.group;
  } else if (route && 'title' in route) {
    title = route.title;
  }
  if (group) {
    const currentGroup = groups[group];
    if (currentGroup) {
      title = currentGroup?.title + (title ? ` - ${title}` : '');
    }
  }
  return title;
};

export const combineAndSortRoutes = ({
  dynamicRoutes,
  navOrder,
  staticRoutes,
}: {
  dynamicRoutes: Record<
    string,
    {
      dynamicGroups?: EntityContentGroupDefinitions;
      loading: boolean;
      routes: DynamicSubRoute[];
    }
  >;
  navOrder: Record<string, number>;
  staticRoutes: readonly DynamicSubRoute[];
}) =>
  sortBy(
    [
      ...staticRoutes,
      ...Object.values(dynamicRoutes)
        .filter(r => !r.loading)
        .flatMap(r => r.routes),
    ],
    [
      route =>
        ('title' in route ? navOrder[route.title.toLocaleLowerCase()] : 9999)
        ?? 9999,
      route => route.path,
    ],
  );

const Toolbar = ({
  entity,
  filteredEntityListWrapper,
  route,
  toolbarElementDefinitions,
}: {
  entity?: Entity;
  filteredEntityListWrapper: EntityListWrapper;
  route?: SubRoute;
  toolbarElementDefinitions: {
    Component: ComponentType<{ entity: Entity }>;
    filter?: (entity: Entity) => boolean;
    id: string;
    path?: string;
  }[];
}) => {
  let toolbar;

  const toolbarElements: ReactElement[] = [];

  if (entity) {
    if (route && hasToolbar(route)) {
      toolbarElements.push(
        <route.toolbarElements entity={entity} key="route-specific-toolbar" />,
      );
    }

    toolbarElements.push(
      ...filteredEntityListWrapper.map(
        ({ id, ToolbarElements: ToolbarComponent }) => (
          <ToolbarComponent entity={entity} key={id} />
        ),
      ),
      ...toolbarElementDefinitions
        .filter(({ path }) => !route?.path || route.path === path)
        .filter(({ filter }) => filter?.(entity) ?? true)
        .map(({ Component, id }) => <Component entity={entity} key={id} />),
    );

    if (toolbarElements.length) {
      toolbar = <OverviewPageToolbar>{toolbarElements}</OverviewPageToolbar>;
    }
  }

  return toolbar;
};

export const filterEntityListByRoute = ({
  entity,
  entityListWrapper,
  route,
}: {
  entity?: Entity;
  entityListWrapper: EntityListWrapper;
  route?: SubRoute;
}) =>
  entityListWrapper
    .filter(({ path }) => !route?.path || route.path === path)
    .filter(({ filter }) => (entity ? (filter?.(entity) ?? true) : false));

export const getEntityLayoutProviders = ({
  filteredEntityListWrapper,
  route,
}: {
  filteredEntityListWrapper: EntityListWrapper;
  route: SubRoute | undefined;
}) => {
  const provider: ComponentType<PropsWithChildren<{ entity: Entity }>>[] =
    filteredEntityListWrapper
      .map(wrapper => wrapper.LayoutWrapperComponent)
      .filter(notEmpty);

  if (route && hasWrapper(route)) {
    provider.push(route.wrapperComponent);
  }
  return provider;
};

export const mergeDynamicGroups = ({
  dynamicRoutes,
  groupDefinitions,
}: {
  dynamicRoutes: DynamicRoute;
  groupDefinitions: EntityContentGroupDefinitions;
}) => {
  const allGroups = { ...groupDefinitions };

  Object.values(dynamicRoutes)?.forEach(dynamicRoute =>
    Object.assign(allGroups, dynamicRoute.dynamicGroups),
  );

  return allGroups;
};

const filterDynamicRoutes = ({
  dynamicRouteDefinitions,
  entity,
}: {
  dynamicRouteDefinitions: DynamicRouteDefinition[];
  entity?: Entity;
}) =>
  dynamicRouteDefinitions.filter(({ filter }) =>
    (entity ? (filter?.(entity) ?? true) : false),
  );
